import { InvoiceData, InvoiceStatusType, InvoiceType } from 'types/invoices';

/** The invoice page's tabs in the new design; Cases keeps the id the Details tab had. */
export const INVOICE_TAB = {
    GENERAL: 'general',
    CASES: 'details',
    TIMECARDS: 'timecards',
    EXPENSES: 'expenses',
} as const;

export type InvoiceTabId = (typeof INVOICE_TAB)[keyof typeof INVOICE_TAB];

export type InvoiceAlertId =
    | 'missing-job-role'
    | 'missing-hourly-rate'
    | 'cap-error'
    | 'unsigned-service-agreement'
    | 'new-timecards'
    | 'cannot-add-new-items'
    | 'new-expenses'
    | 'hourly-rates-changed'
    | 'cannot-update-hourly-rates'
    | 'timecards-added'
    | 'expenses-added'
    | 'hourly-rates-updated'
    | 'fee-arrangement-changed'
    | 'case-waiting'
    | 'postpone-requested'
    | 'postpone-disagreed-continued'
    | 'postpone-rejected'
    | 'invoice-rejected'
    | 'postpone-confirmed'
    | 'signed-by-missing'
    | 'editing';

export interface InvoiceTabAlert {
    tab: InvoiceTabId;
    id: InvoiceAlertId;
    /** Something the user acts on: it leads its tab and counts in the tab's mark. */
    isAction: boolean;
    /** The case an alert is about, for those that come once per case. */
    matterId?: number;
}

/** What a case's summary says about it, by case. */
export interface InvoiceCaseSummaryFlags {
    hasMatterFeeArrangementTypeChanged: boolean;
    /** The signed-in user may review, approve or reject this case of a joint invoice. */
    isMatterStatusUpdatable: boolean;
}

export interface InvoiceAlertInput {
    invoice: Pick<
        InvoiceData,
        | 'invoiceTypeCode'
        | 'invoiceStatusTypeCode'
        | 'isSplit'
        | 'invoiceMatters'
        | 'hasMatterHourlyPriceChanged'
        | 'signedByEmployee'
        | 'isEditable'
    >;
    /** The cases' and items' read-only rule: the page's view mode, a credit note or a split copy. */
    isDetailsReadOnly: boolean;
    hasUninvoicedTimecards: boolean;
    hasUninvoicedExpenses: boolean;
    isTimecardEmployeeMissingRole: boolean;
    isTimecardMissingHourlyRate: boolean;
    successMessages: { newTimecardsAdded: boolean; newExpensesAdded: boolean; hourlyRateUpdated: boolean };
    /** Per case, once its summary has loaded. */
    caseSummaries?: Record<number, InvoiceCaseSummaryFlags | undefined>;
    /** A joint invoice reviewed and approved case by case. */
    isMultiApprovalFlow?: boolean;
    isRequestedToPostpone?: boolean;
    /** Where the approval flow last left the invoice, as `useInvoiceFlow` reads it. */
    flow?: {
        isPostponeDisagreedContinued: boolean;
        isPostponeRejected: boolean;
        isInvoiceRejected: boolean;
        isPostponeConfirmed: boolean;
    };
    /** The tab of the card open in edit, if any: an edit waits for the user too. */
    editingTab?: InvoiceTabId | null;
}

type CaseAlertInput = Pick<
    InvoiceAlertInput,
    'invoice' | 'isDetailsReadOnly' | 'isMultiApprovalFlow' | 'isRequestedToPostpone'
>;
type InvoiceMatter = NonNullable<InvoiceData['invoiceMatters']>[number];

/** Which of a case's alerts its state allows; each then waits on its flag in the case's summary. */
const caseAlertsAllowed = (
    matter: InvoiceMatter,
    { invoice, isDetailsReadOnly, isMultiApprovalFlow = false, isRequestedToPostpone = false }: CaseAlertInput
) => {
    const status = invoice.invoiceStatusTypeCode;
    const isDraft = status === InvoiceStatusType.DRAFT;
    const isWaiting = isDraft ? !matter.reviewedAt : status === InvoiceStatusType.REVIEWED && !matter.approvedAt;
    return {
        feeArrangementChanged:
            invoice.invoiceTypeCode === InvoiceType.DEBIT && isDraft && !matter.reviewedAt && !isDetailsReadOnly,
        caseWaiting: isMultiApprovalFlow && isWaiting && !isRequestedToPostpone,
    };
};

/** Whether a case's summary can raise an alert: no other case's summary is worth loading for the alerts. */
export const caseAlertsNeedSummary = (matter: InvoiceMatter, input: CaseAlertInput) => {
    const allowed = caseAlertsAllowed(matter, input);
    return allowed.feeArrangementChanged || allowed.caseWaiting;
};

/**
 * Every alert the invoice raises, on the tab it concerns. The list keeps the order the classic Details tab draws its
 * alerts in, then the success notices, then what only the new design's tabs show; a tab takes its action cards first,
 * and its mark counts them, whether or not the tab is the one on screen.
 */
export const getInvoiceTabAlerts = ({
    invoice,
    isDetailsReadOnly,
    hasUninvoicedTimecards,
    hasUninvoicedExpenses,
    isTimecardEmployeeMissingRole,
    isTimecardMissingHourlyRate,
    successMessages,
    caseSummaries = {},
    isMultiApprovalFlow = false,
    isRequestedToPostpone = false,
    flow,
    editingTab = null,
}: InvoiceAlertInput): InvoiceTabAlert[] => {
    const { CASES, TIMECARDS, EXPENSES, GENERAL } = INVOICE_TAB;
    const status = invoice.invoiceStatusTypeCode;
    const isDraft = status === InvoiceStatusType.DRAFT;
    const isApproved = !isDraft && status !== InvoiceStatusType.REVIEWED;
    const isFinalized = isApproved && status !== InvoiceStatusType.APPROVED;
    const isDebit = invoice.invoiceTypeCode === InvoiceType.DEBIT;
    const matters = invoice.invoiceMatters ?? [];
    const alerts: InvoiceTabAlert[] = [];
    const add = (tab: InvoiceTabId, id: InvoiceAlertId, isAction = false) => alerts.push({ tab, id, isAction });

    if (isDebit && !invoice.isSplit) {
        if (isTimecardEmployeeMissingRole && !isDetailsReadOnly && !isFinalized) {
            add(TIMECARDS, 'missing-job-role');
        }
        if (isTimecardMissingHourlyRate && !isDetailsReadOnly && !isFinalized) {
            add(TIMECARDS, 'missing-hourly-rate');
        }
        // It holds the next step back, so it is the user's to act on.
        if (matters.some((matter) => matter.hasMatterCapError)) {
            add(CASES, 'cap-error', true);
        }
        if (matters.some((matter) => matter.hasUnsignedServiceAgreement)) {
            add(CASES, 'unsigned-service-agreement');
        }
        if (hasUninvoicedTimecards && !isDetailsReadOnly && !isApproved) {
            add(TIMECARDS, 'new-timecards', true);
        }
        if ((hasUninvoicedTimecards || hasUninvoicedExpenses) && isApproved && !isFinalized && invoice.isEditable) {
            add(hasUninvoicedTimecards ? TIMECARDS : EXPENSES, 'cannot-add-new-items');
        }
        if (hasUninvoicedExpenses && !isDetailsReadOnly && !isApproved) {
            add(EXPENSES, 'new-expenses', true);
        }
        if (invoice.hasMatterHourlyPriceChanged && !isDetailsReadOnly && !isApproved) {
            add(TIMECARDS, 'hourly-rates-changed', true);
        }
        if (invoice.hasMatterHourlyPriceChanged && isApproved && !isFinalized && invoice.isEditable) {
            add(TIMECARDS, 'cannot-update-hourly-rates');
        }
    }

    if (successMessages.newTimecardsAdded) add(TIMECARDS, 'timecards-added');
    if (successMessages.newExpensesAdded) add(EXPENSES, 'expenses-added');
    if (successMessages.hourlyRateUpdated) add(TIMECARDS, 'hourly-rates-updated');

    for (const matter of matters) {
        const summary = caseSummaries[matter.matterId];
        const allowed = caseAlertsAllowed(matter, {
            invoice,
            isDetailsReadOnly,
            isMultiApprovalFlow,
            isRequestedToPostpone,
        });
        if (allowed.feeArrangementChanged && summary?.hasMatterFeeArrangementTypeChanged) {
            alerts.push({
                tab: CASES,
                id: 'fee-arrangement-changed',
                isAction: true,
                matterId: matter.matterId,
            });
        }
        if (allowed.caseWaiting && summary?.isMatterStatusUpdatable) {
            alerts.push({
                tab: CASES,
                id: 'case-waiting',
                isAction: true,
                matterId: matter.matterId,
            });
        }
    }

    // The flow's last word is read, not acted on here: its answer is the band's buttons.
    if (isRequestedToPostpone) add(GENERAL, 'postpone-requested');
    if (flow?.isPostponeDisagreedContinued) add(GENERAL, 'postpone-disagreed-continued');
    if (flow?.isPostponeRejected) add(GENERAL, 'postpone-rejected');
    if (flow?.isInvoiceRejected && !isFinalized) add(GENERAL, 'invoice-rejected');
    if (flow?.isPostponeConfirmed) add(GENERAL, 'postpone-confirmed');

    // The next step waits for it, and General is where it is set.
    if (!invoice.signedByEmployee && invoice.isEditable && !isFinalized) {
        add(GENERAL, 'signed-by-missing', true);
    }
    if (editingTab) {
        add(editingTab, 'editing', true);
    }
    return alerts;
};

/** How many things wait for the user on each tab: its mark. */
export const countTabActions = (alerts: InvoiceTabAlert[]): Partial<Record<InvoiceTabId, number>> =>
    alerts.reduce<Partial<Record<InvoiceTabId, number>>>(
        (counts, alert) => (alert.isAction ? { ...counts, [alert.tab]: (counts[alert.tab] ?? 0) + 1 } : counts),
        {}
    );

/** A tab's alerts as an item tab draws them: those with an action first, then the rest, each in the list's order. */
export const actionsFirst = (alerts: InvoiceTabAlert[]): InvoiceTabAlert[] => [
    ...alerts.filter((alert) => alert.isAction),
    ...alerts.filter((alert) => !alert.isAction),
];
